import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import AdminLayout from "../components/AdminLayout";
import { listAuditLogs, type AuditLog } from "@/lib/admin";

function statusBadge(code: number): { label: string; cls: string } {
  if (code >= 200 && code < 300) return { label: String(code), cls: "bg-ocean/10 text-ocean" };
  if (code >= 400 && code < 500) return { label: String(code), cls: "bg-sand/60 text-charcoal" };
  return { label: String(code), cls: "bg-coral/10 text-coral" };
}

function formatAt(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString("es-MX");
}

export default function AdminAuditoriaPage() {
  const { t } = useTranslation("admin");
  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    listAuditLogs({ search: search.trim() || undefined, limit: 200 })
      .then((data) => { if (!cancelled) setLogs(data); })
      .catch(() => { if (!cancelled) setLogs([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [search]);

  return (
    <AdminLayout title={t("admin.aud.title")}>
      <div className="mb-4">
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t("admin.aud.search")}
          className="w-full sm:w-80 border border-gray-200 rounded-full px-4 py-2 text-sm focus:outline-none focus:border-ocean"
        />
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 p-5 md:p-6 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-100">
              <th className="text-left text-xs font-medium text-gray-400 py-3 pr-4">{t("admin.aud.date")}</th>
              <th className="text-left text-xs font-medium text-gray-400 py-3 pr-4">{t("admin.aud.method")}</th>
              <th className="text-left text-xs font-medium text-gray-400 py-3 pr-4">{t("admin.aud.path")}</th>
              <th className="text-left text-xs font-medium text-gray-400 py-3 pr-4">{t("admin.aud.user")}</th>
              <th className="text-left text-xs font-medium text-gray-400 py-3 pr-4">{t("admin.aud.ip")}</th>
              <th className="text-left text-xs font-medium text-gray-400 py-3">{t("admin.aud.status")}</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} className="py-10 text-center text-gray-400">{t("admin.loading")}</td></tr>
            ) : logs.length === 0 ? (
              <tr><td colSpan={6} className="py-10 text-center text-gray-400">{t("admin.aud.empty")}</td></tr>
            ) : (
              logs.map((l) => {
                const badge = statusBadge(l.statusCode);
                return (
                  <tr key={l.id} className="border-b border-gray-50 last:border-0">
                    <td className="py-3 pr-4 text-gray-500 whitespace-nowrap">{formatAt(l.at)}</td>
                    <td className="py-3 pr-4 font-mono text-xs font-semibold text-charcoal">{l.method}</td>
                    <td className="py-3 pr-4 text-gray-600 font-mono text-xs max-w-xs truncate" title={l.path}>{l.path}</td>
                    <td className="py-3 pr-4 text-gray-600">{l.actorEmail ?? "—"}</td>
                    <td className="py-3 pr-4 text-gray-500 font-mono text-xs">{l.ip ?? "—"}</td>
                    <td className="py-3">
                      <span className={`text-xs font-medium px-2 py-1 rounded-full ${badge.cls}`}>{badge.label}</span>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </AdminLayout>
  );
}
